import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Textarea } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { REASON_FIELD_ID, useContactUsMutation } from '#screens/support/contact/form/ContactUsForm.queries.ts';

type Props = {
  email: string;
  onSubmitted: () => void;
};

const schema = z.object({
  feedback: z.string().refine((x) => x.trim().length > 0, 'What would you like to tell us?'),
});

export function DeletedAccountForm({ email, onSubmitted }: Props) {
  const contactUs = useContactUsMutation();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { feedback: '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const requestBody = JSON.stringify({
        request: {
          requester: {
            name: email,
            email: email,
          },
          custom_fields: [{ id: REASON_FIELD_ID, value: 'feedback' }],
          priority: 'normal',
          subject: `❌ Account deletion feedback from ${email}`,
          comment: { body: data.feedback },
          status: 'closed',
        },
      });
      await contactUs.mutateAsync(requestBody);
      onSubmitted();
    } catch (error) {
      if (
        error instanceof ValidationError &&
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'feedback':
              setError(path, { message });
              return true;
            case undefined: // TODO: should we set a global error here?
            default:
              return false;
          }
        }) === true
      ) {
        return;
      }
      setError('root.global', {
        message: "There was a problem submitting feedback. If you'd still like to reach us, email support@defined.net",
      });
      trackError(error);
    }
  });

  return (
    <AuthLayout>
      <div className="simple-page-content">
        <h1 className="h2">Your account has been deleted</h1>
        <p className="p color-lowlight">
          Thanks for trying Managed Nebula, we’re sorry to hear things didn’t work out. What was the reason for deleting
          your account?
        </p>
        <form action="post" onSubmit={onSubmit}>
          {errors.root?.global?.message && <ErrorMessage className="mb-16">{errors.root.global.message}</ErrorMessage>}
          <Field className="mb-32">
            <Textarea className="width-100" resizable="vertical" rows={6} {...register('feedback')} />
            {errors.feedback?.message && <ErrorMessage>{errors.feedback.message}</ErrorMessage>}
          </Field>
          <Button className="width-100" type="submit">
            Submit
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
